/**
 * Pets core: pure helpers shared by the Pets pages. No DOM access, so the same file runs in the
 * browser (as Lumi.petsCore) and in Node for tests/pets-core.spec.js. In the browser the pet words
 * come from the locale (Lumi.petsSettings.words, set by snippets/pets-head.liquid); in Node they are English.
 */
(function (factory) {
  const core = factory();
  if (typeof module === 'object' && module.exports) {
    module.exports = core;
  } else {
    const Lumi = (window.Lumi = window.Lumi || {});
    Lumi.petsCore = Lumi.petsCore || core;
  }
})(function () {
  const PETS = ['all', 'dog', 'cat'];
  const NAME_MAX = 20;
  const EASE_CM = 2.5;
  const SNUG_CM = 1.5;
  const SHORT_BACK_CM = 3;

  function cleanName(input) {
    if (typeof input !== 'string') return '';
    return input
      .replace(/<[^>]*>/g, '')
      .replace(/[<>]/g, '')
      .replace(/\s+/g, ' ')
      .trim()
      .slice(0, NAME_MAX)
      .trim();
  }

  // The words filled into {who} and {whose}. A locale may replace any of them; {name} is the pet's name.
  const WORDS = {
    whoDog: 'your dog',
    whoCat: 'your cat',
    whoPet: 'your pet',
    whoseDog: "your dog's",
    whoseCat: "your cat's",
    whosePet: 'their',
    whoseName: "{name}'s",
    whoseNameS: "{name}'",
  };

  function wordsFor(words) {
    const given = words || (typeof window !== 'undefined' && window.Lumi && window.Lumi.petsSettings && window.Lumi.petsSettings.words) || {};
    const out = Object.assign({}, WORDS);
    Object.keys(WORDS).forEach((key) => {
      if (typeof given[key] === 'string' && given[key]) out[key] = given[key];
    });
    return out;
  }

  function possessive(name, words) {
    const w = wordsFor(words);
    return (/s$/i.test(name) ? w.whoseNameS : w.whoseName).replace('{name}', () => name);
  }

  function petWord(pet, words) {
    const w = wordsFor(words);
    if (pet === 'dog') return w.whoDog;
    if (pet === 'cat') return w.whoCat;
    return w.whoPet;
  }

  function whose(profile, words) {
    const w = wordsFor(words);
    if (profile.name) return possessive(profile.name, w);
    if (profile.pet === 'dog') return w.whoseDog;
    if (profile.pet === 'cat') return w.whoseCat;
    return w.whosePet;
  }

  function fillTemplate(template, profile, words) {
    const p = profile || { pet: 'all', name: '' };
    const w = wordsFor(words);
    const who = p.name || petWord(p.pet, w);
    const theirs = whose(p, w);
    // One pass with a function, so a name goes in exactly as typed (a $ in it is not a pattern).
    return String(template).replace(/\{(who|whose)\}/g, (match, key) => (key === 'who' ? who : theirs));
  }

  const positive = (v) => typeof v === 'number' && Number.isFinite(v) && v > 0;

  function parseProfile(raw) {
    const out = { pet: 'all', name: '' };
    let data = null;
    try {
      data = raw ? JSON.parse(raw) : null;
    } catch (e) {
      data = null;
    }
    if (!data || typeof data !== 'object') return out;
    if (PETS.includes(data.pet)) out.pet = data.pet;
    out.name = cleanName(data.name);
    ['chestCm', 'backCm', 'weightLb'].forEach((key) => {
      if (positive(data[key])) out[key] = data[key];
    });
    return out;
  }

  function applyUrlParams(profile, params) {
    const out = Object.assign({}, profile);
    const pet = params.get('pet');
    if (pet === 'dog' || pet === 'cat') out.pet = pet;
    const name = cleanName(params.get('name'));
    if (name) out.name = name;
    return out;
  }

  // Products carry either the plain tags (Dog, Cat) or the import's tags (cp-dogs, cp-cats).
  function petOfTags(tags) {
    const lower = (tags || []).map((t) => String(t).toLowerCase());
    const isDog = lower.includes('dog') || lower.includes('cp-dogs');
    const isCat = lower.includes('cat') || lower.includes('cp-cats');
    if (isDog && !isCat) return 'dog';
    if (isCat && !isDog) return 'cat';
    return 'both';
  }

  function fits(productPet, chosen) {
    return chosen === 'all' || productPet === 'both' || productPet === chosen;
  }

  function recommendSize(chart, profile) {
    const rows = (chart || []).filter((r) => r && r.available !== false);
    if (!rows.length || !profile) return null;
    const result = { size: null, basis: 'chest', snug: false, next: null, shortBody: false, tooBig: false };
    if (positive(profile.chestCm)) {
      const need = profile.chestCm + EASE_CM;
      const i = rows.findIndex((r) => r.chest_cm >= need);
      if (i === -1) return Object.assign(result, { tooBig: true });
      const row = rows[i];
      const next = rows[i + 1];
      result.size = row.size;
      result.next = next ? next.size : null;
      result.snug = Boolean(next) && row.chest_cm - need < SNUG_CM;
      result.shortBody = positive(profile.backCm) && typeof row.back_cm === 'number' && row.back_cm < profile.backCm - SHORT_BACK_CM;
      return result;
    }
    if (positive(profile.weightLb)) {
      result.basis = 'weight';
      const i = rows.findIndex((r) => typeof r.max_lb === 'number' && profile.weightLb <= r.max_lb);
      if (i === -1) return Object.assign(result, { tooBig: true });
      result.size = rows[i].size;
      result.next = rows[i + 1] ? rows[i + 1].size : null;
      return result;
    }
    return null;
  }

  function kitSavingCents(pieceCents, kitCents) {
    const sum = (pieceCents || []).reduce((a, b) => a + b, 0);
    return Math.max(0, Math.round(sum - kitCents));
  }

  function isBeforeDeadline(now, deadline, timeZone) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(String(deadline))) return false;
    try {
      const today = new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).format(now);
      return today <= deadline;
    } catch (e) {
      return false;
    }
  }

  return { EASE_CM, cleanName, possessive, petWord, whose, fillTemplate, parseProfile, applyUrlParams, petOfTags, fits, recommendSize, kitSavingCents, isBeforeDeadline };
});
